import React from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const OwnerTab = ({ formData, onChange, validationErrors, onBlur, customFields, errors }) => {
  const ownerTypes = [
    { value: 'individual', label: 'فرد' },
    { value: 'company', label: 'شركة' },
    { value: 'government', label: 'جهة حكومية' }
  ];

  const listingSourceOptions = [
    { value: 'direct_owner', label: 'مباشرة من المالك' },
    { value: 'agent', label: 'عن طريق وكيل' },
    { value: 'referral', label: 'إحالة' },
    { value: 'marketing', label: 'حملة تسويقية' },
    { value: 'other', label: 'أخرى' }
  ];

  const dealingWithOptions = [
    { value: 'owner', label: 'المالك مباشرة' },
    { value: 'agent', label: 'وكيل العقار' },
    { value: 'both', label: 'كلاهما' }
  ];

  const approvalStatusOptions = [
    { value: 'approved', label: 'تمت الموافقة' },
    { value: 'pending', label: 'قيد الانتظار' },
    { value: 'not_required', label: 'غير مطلوبة' },
    { value: 'rejected', label: 'مرفوضة' }
  ];

  const ownerCustomFields = customFields?.filter(field => field?.category === 'owner');

  const renderCustomField = (field) => {
    const value = formData?.customFields?.[field?.id] || '';
    const error = errors?.[`customFields.${field?.id}`];

    switch (field?.type) {
      case 'textarea':
        return (
          <div key={field?.id} className="md:col-span-2">
            <label className="block text-sm font-medium text-foreground mb-2">
              {field?.label} {field?.required && <span className="text-error">*</span>}
            </label>
            <textarea
              className="w-full min-h-24 p-3 rounded-lg border border-input bg-background text-foreground resize-y focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder={`أدخل ${field?.label}`}
              value={value}
              onChange={(e) => onChange(`customFields.${field?.id}`, e?.target?.value)}
            />
            {error && <p className="text-sm text-error mt-1">{error}</p>}
          </div>
        );
      default:
        return (
          <Input
            key={field?.id}
            label={field?.label}
            type={field?.type || 'text'}
            placeholder={`أدخل ${field?.label}`}
            value={value}
            onChange={(e) => onChange(`customFields.${field?.id}`, e?.target?.value)}
            error={error}
            required={field?.required}
          />
        );
    }
  };

  const handleWhatsAppContact = () => {
    const phone = formData?.ownerPhone?.replace(/\D/g, '');
    if (phone) {
      window.open(`https://wa.me/${phone}`, '_blank');
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        <Input
          label="اسم المالك"
          type="text"
          placeholder="أدخل اسم المالك"
          value={formData?.ownerName}
          onChange={(e) => onChange('ownerName', e?.target?.value)}
          onBlur={() => onBlur?.('ownerName')}
          error={errors?.ownerName}
          required
        />

        <Select
          label="نوع المالك"
          options={ownerTypes}
          value={formData?.ownerType}
          onChange={(value) => onChange('ownerType', value)}
          error={errors?.ownerType}
          required
        />

        <Input
          label="رقم الهوية / السجل التجاري"
          type="text"
          placeholder="أدخل رقم الهوية"
          value={formData?.ownerIdNumber}
          onChange={(e) => onChange('ownerIdNumber', e?.target?.value)}
          error={errors?.ownerIdNumber}
          required
        />

        <Input
          label="رقم الجوال"
          type="tel"
          placeholder="+966 5X XXX XXXX"
          value={formData?.ownerPhone}
          onChange={(e) => onChange('ownerPhone', e?.target?.value)}
          onBlur={() => onBlur?.('ownerPhone')}
          error={errors?.ownerPhone}
          required
        />

        <Input
          label="رقم جوال بديل"
          type="tel"
          placeholder="+966 5X XXX XXXX"
          value={formData?.ownerAlternatePhone}
          onChange={(e) => onChange('ownerAlternatePhone', e?.target?.value)}
        />

        <Input
          label="البريد الإلكتروني"
          type="email"
          placeholder="owner@example.com"
          value={formData?.ownerEmail}
          onChange={(e) => onChange('ownerEmail', e?.target?.value)}
          onBlur={() => onBlur?.('ownerEmail')}
          error={errors?.ownerEmail}
        />

        <Input
          label="العنوان"
          type="text"
          placeholder="أدخل عنوان المالك"
          value={formData?.ownerAddress}
          onChange={(e) => onChange('ownerAddress', e?.target?.value)}
        />
      </div>
      <div className="bg-muted/50 p-4 rounded-lg border border-border">
        <h3 className="text-base font-heading font-semibold text-foreground mb-3">
          التواصل السريع
        </h3>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={handleWhatsAppContact}
            disabled={!formData?.ownerPhone}
            iconName="MessageCircle"
            iconPosition="left"
          >
            واتساب
          </Button>
          <Button
            variant="outline"
            onClick={() => window.location.href = `tel:${formData?.ownerPhone}`}
            disabled={!formData?.ownerPhone}
            iconName="Phone"
            iconPosition="left"
          >
            اتصال
          </Button>
          <Button
            variant="outline"
            onClick={() => window.location.href = `mailto:${formData?.ownerEmail}`}
            disabled={!formData?.ownerEmail}
            iconName="Mail"
            iconPosition="left"
          >
            بريد إلكتروني
          </Button>
        </div>
      </div>

      <div className="border-t border-border pt-4">
        <h3 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
          <span className="w-1 h-4 bg-primary rounded"></span>
          معلومات مصدر القائمة
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          <Input
            label="من أحضر هذا العقار"
            type="text"
            placeholder="اسم الموظف أو المصدر"
            value={formData?.listingBroughtBy}
            onChange={(e) => onChange('listingBroughtBy', e?.target?.value)}
            description="الموظف أو الشخص الذي أحضر هذا العقار"
          />

          <Select
            label="مصدر القائمة"
            options={listingSourceOptions}
            value={formData?.listingSource}
            onChange={(value) => onChange('listingSource', value)}
            description="كيف تم الحصول على هذا العقار"
          />

          <Select
            label="نتعامل مع"
            options={dealingWithOptions}
            value={formData?.dealingWith}
            onChange={(value) => onChange('dealingWith', value)}
            required
            description="هل نتعامل مع المالك أو الوكيل"
          />

          <Select
            label="حالة موافقة المالك"
            options={approvalStatusOptions}
            value={formData?.ownerApprovalStatus}
            onChange={(value) => onChange('ownerApprovalStatus', value)}
            required
            description="هل لدينا موافقة مباشرة من المالك"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          ملاحظات عن المالك
        </label>
        <textarea
          className="w-full min-h-20 p-3 rounded-lg border border-input bg-background text-foreground resize-y focus:outline-none focus:ring-2 focus:ring-ring"
          placeholder="أي ملاحظات أو تفاصيل إضافية عن المالك..."
          value={formData?.ownerNotes}
          onChange={(e) => onChange('ownerNotes', e?.target?.value)}
        />
      </div>

      {ownerCustomFields?.length > 0 && (
        <div className="border-t border-border pt-4">
          <h3 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
            <span className="w-1 h-4 bg-primary rounded"></span>
            الحقول المخصصة
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            {ownerCustomFields?.map(field => renderCustomField(field))}
          </div>
        </div>
      )}

      <div className="bg-muted/50 p-4 rounded-lg border border-border">
        <div className="flex items-start gap-3">
          <Icon name="Shield" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-2">حماية البيانات:</p>
            <p>جميع معلومات المالك محمية ومشفرة. يتم استخدامها فقط لأغراض إدارة العقار والتواصل الرسمي.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OwnerTab;